/**
 * API Hooks Index
 * Central re-export point for all API hooks
 * Maintains backward compatibility with existing imports
 */

// ============================================================================
// TYPES
// ============================================================================

export type {
  CalendarEvent,
  GoogleCalendarEvent,
  MicrosoftCalendarEvent,
  CalendarDataParams,
  AvailableDay,
  BookingData,
  OpenSlotsParams,
  AccountStatus,
  ResetResponse,
  RemoveConnectionResponse,
  OAuthSetupParams,
  SearchResult,
  CRMSearchParams,
  CRMSearchResult,
  ApiError,
  QueryResponse,
  MutationResponse,
} from "./types.js";

// ============================================================================
// CONFIGURATION & UTILITIES
// ============================================================================

export {
  API_BASE_PATH,
  QUERY_CONFIG,
  RETRY_CONFIG,
  OAUTH_CONFIG,
  ERROR_CODES,
  ENDPOINTS,
  TOAST_MESSAGES,
} from "./config.js";

export { queryKeys, getCalendarQueryKeysToInvalidate, getAccountRelatedQueryKeysToInvalidate } from "./query-keys.js";

export {
  buildQueryParams,
  isCalendarNotConnectedError,
  getErrorMessage,
  formatErrorResponse,
  getAuthTokenWithRetry,
  logDebug,
  logWarning,
  logError,
  countEventsBySource,
  validateCalendarEvents,
} from "./utils.js";

// ============================================================================
// CALENDAR HOOKS
// ============================================================================

export {
  useGoogleCalendarData,
  useMicrosoftCalendarData,
  useExternalGoogleEvents,
  useExternalMicrosoftEvents,
  useCombinedCalendarData,
} from "./calendar/queries.js";

export {
  useCreateGoogleEvent,
  useCreateMicrosoftEvent,
  useDeleteGoogleEvent,
  useDeleteMicrosoftEvent,
  useDeleteEvent,
  useUpdateGoogleEvent,
  useUpdateMicrosoftEvent,
  useUpdateEvent,
} from "./calendar/mutations.js";

// ============================================================================
// BOOKING HOOKS
// ============================================================================

export { useOpenSlots } from "./booking/queries.js";

export { useBookingMutation } from "./booking/mutations.js";

// ============================================================================
// ACCOUNT HOOKS
// ============================================================================

export { useAccountStatus } from "./account/queries.js";

export { useResetAccountMutation, useRemoveConnectionMutation } from "./account/mutations.js";

export { useGoogleOAuthSetup, useMicrosoftOAuthSetup } from "./account/oauth.js";

// ============================================================================
// CRM HOOKS
// ============================================================================

export { useCRMSearch } from "./crm/mutations.js";
